import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar, MapPin, Clock, DollarSign, Users, Shield, CheckCircle,
  Briefcase, ChevronRight, Navigation, Mail, Phone, Edit3, Check, X
} from "lucide-react";

const statusConfig = {
  HIRED: { label: "Confirmed", bg: "from-blue-500 to-blue-600" },
  PARTIALLY_HIRED: { label: "Partially Hired", bg: "from-blue-500 to-blue-600" },
  PENDING_COMPLETION: { label: "Pending Completion", bg: "from-purple-500 to-purple-600" },
  RELEASE_PAYMENT: { label: "Awaiting Payment", bg: "from-emerald-500 to-emerald-600" },
};

const formatDate = (dateStr) => {
  if (!dateStr) return "TBD";
  try {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch { return dateStr; }
};

const formatDateLong = (dateStr) => {
  if (!dateStr) return "TBD";
  try {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return dateStr; }
};

const isJobDatePassed = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return new Date(dateStr + "T00:00:00") <= today;
};

const getDaysUntil = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.ceil((new Date(dateStr + "T00:00:00") - today) / (1000 * 60 * 60 * 24));
};

const openInMaps = (address) => {
  window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, "_blank");
};

const formatMoney = (n) => {
  const num = Number(n);
  if (!isFinite(num)) return "0.00";
  return num.toFixed(2);
};

const formatTime12 = (t) => {
  if (!t) return "TBC";
  try {
    const [h, m] = t.split(":");
    const hr = parseInt(h);
    const ampm = hr >= 12 ? "PM" : "AM";
    const hr12 = hr % 12 || 12;
    return `${hr12}:${m} ${ampm}`;
  } catch { return t; }
};

const JobCommsJobDetails = ({ job, userRole, compact = false }) => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("shootzu_user") || "{}");

  // Per-date schedule state
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [schedules, setSchedules] = useState(job?.date_schedules || {});
  const [globalStart, setGlobalStart] = useState(job?.start_time || "");
  const [globalEnd, setGlobalEnd] = useState(job?.end_time || "");
  const [saving, setSaving] = useState(false);

  if (!job) return null;

  const crewMembers = job.crew_members || [];
  const isAdvanced = job.is_advanced_job || crewMembers.length > 1;
  const activeCrew = crewMembers.filter(cm => cm.hired && cm.hired_user_id !== cm.cancelled_crew_user_id);
  const daysUntil = getDaysUntil(job.date);
  const config = statusConfig[job.status] || { label: job.status, bg: "from-gray-500 to-gray-600" };
  const dates = (job.dates && job.dates.length > 0) ? job.dates : (job.date ? [job.date] : []);
  const isMultiDay = dates.length > 1;

  // Payment: use job-level values (backend now returns correct values per user)
  const payRate = job.pay_rate || 0;
  const hours = job.hours || 0;
  const totalAmount = job.total_amount || job.budget || 0;

  const handleSaveSchedule = async () => {
    setSaving(true);
    try {
      const payload = { user_email: user.email };
      if (isMultiDay) {
        payload.date_schedules = schedules;
      } else {
        payload.start_time = globalStart;
        payload.end_time = globalEnd;
      }
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/job-comms/${job.id}/update-times`,
        payload
      );
      toast.success("Schedule updated");
      setEditingSchedule(false);
    } catch (err) {
      toast.error("Failed to update schedule");
    } finally {
      setSaving(false);
    }
  };

  const getDateSchedule = (date) => schedules[date] || {};
  const setDateSchedule = (date, field, value) => {
    setSchedules(prev => ({ ...prev, [date]: { ...prev[date], [field]: value } }));
  };

  // Dark Command Deck tokens
  const card = { background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.08)", borderRadius: 14, overflow: "hidden" };
  const labelStyle = { fontSize: 11, fontWeight: 700, letterSpacing: ".6px", color: "rgba(255,255,255,.45)", textTransform: "uppercase" };
  const inputStyle = { background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", color: "#fff", colorScheme: "dark" };
  const statusInk = { HIRED: "#22D3EE", PARTIALLY_HIRED: "#22D3EE", PENDING_COMPLETION: "#C084FC", RELEASE_PAYMENT: "#67C6B4" }[job.status] || "#22D3EE";
  const countdownColor = (du) =>
    du == null ? "rgba(255,255,255,.45)"
    : du < 0 ? "rgba(255,255,255,.4)"
    : du === 0 ? "#F87171"
    : du <= 3 ? "#FBBF24"
    : "#67C6B4";

  return (
    <div className="overflow-y-auto h-full">
      <div className={`${compact ? "p-3 space-y-3" : "p-4 sm:p-5 space-y-4"}`}>

        {/* Status + Date Card */}
        <div style={card}>
          <div className="flex items-center justify-between px-4 py-2.5" style={{ background: "rgba(255,255,255,.03)", borderBottom: "1px solid rgba(255,255,255,.07)" }}>
            <span className="inline-flex items-center gap-2 text-sm font-bold" style={{ color: "#fff" }}>
              <span className="rounded-full" style={{ width: 7, height: 7, background: statusInk, boxShadow: `0 0 8px ${statusInk}` }} />
              {config.label}
            </span>
            <div className="flex items-center gap-2">
              {job.job_type && <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase" style={{ background: "rgba(255,255,255,.07)", color: "rgba(255,255,255,.7)" }}>{job.job_type}</span>}
              {job.requires_wwcc && <span className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold" style={{ background: "rgba(251,191,36,.16)", color: "#FBBF24" }}><Shield className="w-3 h-3" /> WWCC</span>}
            </div>
          </div>
          <div className="px-4 py-3">
            {!isMultiDay ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" style={{ color: "rgba(255,255,255,.45)" }} />
                  <span className="text-sm font-medium" style={{ color: "#fff" }}>{formatDateLong(dates[0])}</span>
                </div>
                {daysUntil !== null && (
                  <span className="px-2.5 py-1 rounded-lg text-xs font-bold" style={{ color: countdownColor(daysUntil), background: "rgba(255,255,255,.05)" }}>
                    {daysUntil < 0 ? `${Math.abs(daysUntil)}d ago` : daysUntil === 0 ? "TODAY" : `${daysUntil}d to go`}
                  </span>
                )}
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4" style={{ color: "rgba(255,255,255,.45)" }} />
                  <span className="text-sm font-semibold" style={{ color: "#fff" }}>{dates.length} Day Job</span>
                </div>
                <div className="space-y-1 mt-2">
                  {dates.map((d, i) => {
                    const du = getDaysUntil(d);
                    return (
                      <div key={i} className="flex items-center justify-between py-1" style={i > 0 ? { borderTop: "1px solid rgba(255,255,255,.07)" } : undefined}>
                        <span className="text-xs" style={{ color: "rgba(255,255,255,.7)" }}>Day {i + 1}: {formatDate(d)}</span>
                        {du !== null && (
                          <span className="text-[10px] font-semibold" style={{ color: countdownColor(du) }}>
                            {du < 0 ? "Past" : du === 0 ? "TODAY" : `${du}d`}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Schedule (Start/End Times) */}
        <div style={card}>
          <div className="flex items-center justify-between px-4 py-2.5" style={{ background: "rgba(40,192,208,.06)", borderBottom: "1px solid rgba(255,255,255,.07)" }}>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" style={{ color: "#28C0D0" }} />
              <span style={labelStyle}>Schedule</span>
            </div>
            {userRole === "business" && !editingSchedule && (
              <button onClick={() => setEditingSchedule(true)} className="p-1 rounded-md transition-colors" style={{ color: "#28C0D0" }}>
                <Edit3 className="w-3.5 h-3.5" />
              </button>
            )}
            {editingSchedule && (
              <div className="flex items-center gap-1">
                <button onClick={handleSaveSchedule} disabled={saving} className="p-1 rounded-md" style={{ color: "#67C6B4" }}><Check className="w-4 h-4" /></button>
                <button onClick={() => { setEditingSchedule(false); setSchedules(job.date_schedules || {}); setGlobalStart(job.start_time || ""); setGlobalEnd(job.end_time || ""); }} className="p-1 rounded-md" style={{ color: "rgba(255,255,255,.5)" }}><X className="w-4 h-4" /></button>
              </div>
            )}
          </div>

          <div>
            {isMultiDay ? (
              /* Per-date schedule for multi-day jobs */
              <div>
                {dates.map((d, i) => {
                  const sched = getDateSchedule(d);
                  const startVal = sched.start || "";
                  const endVal = sched.end || "";
                  return (
                    <div key={i} className="px-4 py-2.5" style={i > 0 ? { borderTop: "1px solid rgba(255,255,255,.07)" } : undefined}>
                      <p className="text-[10px] font-semibold mb-1.5" style={{ color: "rgba(255,255,255,.45)" }}>Day {i + 1} — {formatDate(d)}</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: "rgba(255,255,255,.4)" }}>Start</p>
                          {editingSchedule ? (
                            <input type="time" value={startVal} onChange={(e) => setDateSchedule(d, "start", e.target.value)}
                              className="w-full text-xs font-semibold rounded-lg px-2 py-1.5 focus:outline-none" style={inputStyle} />
                          ) : (
                            <p className="text-sm font-bold" style={{ color: startVal ? "#fff" : "rgba(255,255,255,.35)" }}>{formatTime12(startVal)}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: "rgba(255,255,255,.4)" }}>End</p>
                          {editingSchedule ? (
                            <input type="time" value={endVal} onChange={(e) => setDateSchedule(d, "end", e.target.value)}
                              className="w-full text-xs font-semibold rounded-lg px-2 py-1.5 focus:outline-none" style={inputStyle} />
                          ) : (
                            <p className="text-sm font-bold" style={{ color: endVal ? "#fff" : "rgba(255,255,255,.35)" }}>{formatTime12(endVal)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Single date schedule */
              <div className="grid grid-cols-2">
                <div className="p-3 text-center" style={{ borderRight: "1px solid rgba(255,255,255,.07)" }}>
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: "rgba(255,255,255,.45)" }}>Start Time</p>
                  {editingSchedule ? (
                    <input type="time" value={globalStart} onChange={(e) => setGlobalStart(e.target.value)}
                      className="w-full text-center text-sm font-bold rounded-lg px-2 py-1.5 focus:outline-none" style={inputStyle} />
                  ) : (
                    <p className="text-base font-black" style={{ color: globalStart ? "#fff" : "rgba(255,255,255,.35)" }}>{formatTime12(globalStart)}</p>
                  )}
                </div>
                <div className="p-3 text-center">
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: "rgba(255,255,255,.45)" }}>End Time</p>
                  {editingSchedule ? (
                    <input type="time" value={globalEnd} onChange={(e) => setGlobalEnd(e.target.value)}
                      className="w-full text-center text-sm font-bold rounded-lg px-2 py-1.5 focus:outline-none" style={inputStyle} />
                  ) : (
                    <p className="text-base font-black" style={{ color: globalEnd ? "#fff" : "rgba(255,255,255,.35)" }}>{formatTime12(globalEnd)}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Crew Hired */}
        {activeCrew.length > 0 && (
          <div style={card}>
            <div className="flex items-center gap-2 px-4 py-2.5" style={{ background: "rgba(103,198,180,.07)", borderBottom: "1px solid rgba(255,255,255,.07)" }}>
              <Users className="w-4 h-4" style={{ color: "#67C6B4" }} />
              <span style={labelStyle}>Crew Hired</span>
            </div>
            <div className="px-4 py-3">
              <div className="space-y-2">
                {activeCrew.map((cm, i) => {
                  const role = (cm.role || "Crew").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
                  const hours = cm.hours || 0;
                  return (
                    <div key={i} className="flex items-center flex-wrap gap-x-2 gap-y-1 text-sm" style={i > 0 ? { paddingTop: 8, borderTop: "1px solid rgba(255,255,255,.07)" } : undefined}>
                      <span className="font-bold" style={{ color: "#fff" }}>
                        {cm.hired_name || "TBD"}
                      </span>
                      {cm.hired_email && (
                        <a href={`mailto:${cm.hired_email}`} title={cm.hired_email} className="p-1 rounded-md transition-colors" style={{ background: "rgba(255,255,255,.06)", color: "rgba(255,255,255,.7)" }}>
                          <Mail className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {cm.hired_phone && (
                        <a href={`tel:${cm.hired_phone}`} title={cm.hired_phone} className="p-1 rounded-md transition-colors" style={{ background: "rgba(255,255,255,.06)", color: "rgba(255,255,255,.7)" }}>
                          <Phone className="w-3.5 h-3.5" />
                        </a>
                      )}
                      <span style={{ color: "rgba(255,255,255,.3)" }}>·</span>
                      <span className="font-medium" style={{ color: "#67C6B4" }}>
                        {role}
                      </span>
                      <span style={{ color: "rgba(255,255,255,.3)" }}>·</span>
                      <span className="font-semibold" style={{ color: "rgba(255,255,255,.75)" }}>
                        {hours} {hours === 1 ? "hour" : "hours"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Location */}
        {job.location_address && (
          <div style={card}>
            <div className="flex items-center justify-between p-3.5">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(248,113,113,.14)" }}>
                  <MapPin className="w-4 h-4" style={{ color: "#F87171" }} />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,.45)" }}>Location</p>
                  <p className="text-sm font-medium truncate" style={{ color: "#fff" }}>{job.location_address}</p>
                </div>
              </div>
              <button onClick={() => openInMaps(job.location_address)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all flex-shrink-0 ml-2" style={{ background: "rgba(40,192,208,.12)", color: "#28C0D0" }}>
                <Navigation className="w-3.5 h-3.5" /> Maps
              </button>
            </div>
          </div>
        )}

        {/* Description */}
        {job.description && (
          <div style={{ ...card, padding: 16 }}>
            <h3 className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "rgba(255,255,255,.45)" }}>Description</h3>
            <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: "rgba(255,255,255,.7)" }}>{job.description}</p>
          </div>
        )}

        {/* Crew Positions */}
        {isAdvanced && activeCrew.length > 0 && (
          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-wider mb-2 px-1" style={{ color: "rgba(255,255,255,.45)" }}>Crew ({activeCrew.length})</h3>
            <div className="space-y-2">
              {activeCrew.map((cm, i) => (
                <div key={i} style={card}>
                  <div className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: "rgba(103,198,180,.15)", color: "#67C6B4", boxShadow: "0 0 0 2px rgba(103,198,180,.2)" }}>
                        {(cm.hired_name || "?")[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-bold" style={{ color: "#fff" }}>{cm.hired_name || "TBD"}</p>
                        <p className="text-xs" style={{ color: "#67C6B4" }}>{(cm.role || "Crew").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</p>
                      </div>
                    </div>
                    {(cm.subtotal || cm.ratePerHour) && (
                      <div className="text-right">
                        <p className="text-sm font-black" style={{ color: "#fff" }}>${formatMoney(cm.subtotal || 0)}</p>
                        <p className="text-[10px]" style={{ color: "rgba(255,255,255,.45)" }}>${formatMoney(cm.ratePerHour || 0)}/hr x {cm.hours || 0}h</p>
                      </div>
                    )}
                  </div>
                  {cm.hired_email && (
                    <div className="flex items-center px-3 py-2" style={{ borderTop: "1px solid rgba(255,255,255,.07)", background: "rgba(255,255,255,.02)" }}>
                      <a href={`mailto:${cm.hired_email}`} className="flex items-center gap-1 text-[11px]" style={{ color: "rgba(255,255,255,.6)" }}>
                        <Mail className="w-3 h-3" /> {cm.hired_email}
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Summary */}
        {(payRate > 0 || totalAmount > 0) && (
          <div style={{ ...card, border: "1px solid rgba(103,198,180,.28)" }}>
            <div className="px-4 py-2.5" style={{ background: "rgba(103,198,180,.1)", borderBottom: "1px solid rgba(103,198,180,.18)" }}>
              <h3 style={{ ...labelStyle, color: "#67C6B4" }}>Payment Summary</h3>
            </div>
            <div className="px-4 py-3 space-y-2">
              {payRate > 0 && (
                <div className="flex justify-between">
                  <span className="text-xs" style={{ color: "rgba(255,255,255,.6)" }}>Rate</span>
                  <span className="text-xs font-semibold" style={{ color: "#fff" }}>${formatMoney(payRate)}/hr</span>
                </div>
              )}
              {hours > 0 && (
                <div className="flex justify-between">
                  <span className="text-xs" style={{ color: "rgba(255,255,255,.6)" }}>Hours</span>
                  <span className="text-xs font-semibold" style={{ color: "#fff" }}>{hours}h</span>
                </div>
              )}
              {isAdvanced && activeCrew.length > 0 && userRole === "business" && (
                <div className="flex justify-between">
                  <span className="text-xs" style={{ color: "rgba(255,255,255,.6)" }}>Positions</span>
                  <span className="text-xs font-semibold" style={{ color: "#fff" }}>{activeCrew.length}</span>
                </div>
              )}
              {totalAmount > 0 && (
                <div style={{ borderTop: "1px solid rgba(103,198,180,.18)", paddingTop: 8 }}>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold" style={{ color: "#67C6B4" }}>
                      {userRole === "crew" ? "Your Total" : "Job Total"}
                    </span>
                    <span className="text-lg font-black" style={{ color: "#67C6B4" }}>${formatMoney(totalAmount)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default JobCommsJobDetails;
